import type {
  LessonCallDetail,
  TranscriptFragment,
} from "@repo/app-types/client/client";
import { TranscriptFragmentRole } from "@repo/app-types/entities/transcriptFragment";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { TextSkeleton } from "@repo/ui/Skeletons/skeletons";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { AUDIO_URL_REFRESH_INTERVAL_MS } from "../../api/entities/recording.js";
import {
  useTrainingParticipantLessonCallsDetailsQuery,
  useTrainingParticipantLessonTranscriptQuery,
} from "../../api/entities/trainingParticipant.js";
import type { AudioPlayerSource } from "../../components/audioPlayer/context.js";
import { useAudioPlayer } from "../../components/audioPlayer/hook.js";
import { AudioPlayerPlayButton } from "../../components/audioPlayer/playButton.js";
import { PlayableTranscriptMessage } from "../../components/transcript/playableTranscriptMessage.js";
import { TranscriptCallMarker } from "../../components/transcript/transcriptCallMarker.js";
import { TranscriptContainer } from "../../components/transcript/transcriptContainer.js";
import { TranscriptMessage } from "../../components/transcript/transcriptMessage.js";

export interface LessonTranscriptProps {
  projectId: string;
  title: string;
  participantId: string;
  participantName: string;
  interviewSectionDefinitionId: string;
  /** Specific completed attempt to view. When omitted, the server returns the most recently completed attempt. */
  interviewSectionResultId?: string;
}

export const LessonTranscript: FC<LessonTranscriptProps> = (props) => {
  const {
    currentData: transcript,
    isFetching: isLoadingTranscripts,
    isError: isErrorTranscripts,
  } = useTrainingParticipantLessonTranscriptQuery({
    projectId: props.projectId,
    participantId: props.participantId,
    interviewSectionDefinitionId: props.interviewSectionDefinitionId,
    interviewSectionResultId: props.interviewSectionResultId,
  });

  const { data: callsDetails } = useTrainingParticipantLessonCallsDetailsQuery(
    {
      projectId: props.projectId,
      participantId: props.participantId,
      interviewSectionDefinitionId: props.interviewSectionDefinitionId,
      interviewSectionResultId: props.interviewSectionResultId,
    },
    // Recording URLs are presigned with a limited expiry
    { pollingInterval: AUDIO_URL_REFRESH_INTERVAL_MS },
  );

  const audioPlayerSources: readonly AudioPlayerSource[] | undefined = useMemo(
    () =>
      callsDetails?.map((call, callIndex, { length: callCount }) => ({
        key: call.id,
        title: props.title,
        subtitle: `Call ${callIndex + 1} of ${callCount}`,
        sortTimestamp: DateTime.fromISO(call.initiated_at).toMillis(),
        src: call.recording_url,
      })),
    [props.title, callsDetails],
  );

  useAudioPlayer({
    sources: audioPlayerSources,
  });

  const isVoiceTranscriptFragments =
    transcript && "voice_transcript_fragments" in transcript;

  const voiceTimeline = isVoiceTranscriptFragments
    ? buildVoiceTimeline(
        transcript.voice_transcript_fragments,
        callsDetails ?? [],
      )
    : undefined;

  const renderVoiceTimelineItem = (
    item: VoiceTimelineItem,
    index: number,
    timeline: VoiceTimelineItem[],
  ) => {
    switch (item.type) {
      case "fragment": {
        const callId = item.callId;
        const relativeTime = item.relativeTime;
        const speaker =
          item.fragment.role === TranscriptFragmentRole.AGENT
            ? "Agent"
            : item.fragment.role === TranscriptFragmentRole.PARTICIPANT
              ? props.participantName
              : "Unknown speaker";

        if (callId !== null && relativeTime !== null) {
          return (
            <PlayableTranscriptMessage
              key={item.fragment.id}
              sourceKey={callId}
              time={relativeTime}
              nextTime={findNextTimeOffsetSeconds(timeline, index, callId)}
              speakerName={speaker}
            >
              {item.fragment.text_transcript}
            </PlayableTranscriptMessage>
          );
        }

        return (
          <TranscriptMessage key={item.fragment.id} speakerName={speaker}>
            {item.fragment.text_transcript}
          </TranscriptMessage>
        );
      }
      case "call":
        return (
          <TranscriptCallMarker
            key={item.call.id}
            type="started"
            formattedTime={DateTime.fromISO(
              item.call.initiated_at,
            ).toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
            action={<AudioPlayerPlayButton sourceKey={item.call.id} />}
          />
        );
      default:
        assertUnreachable(item);
    }
  };

  return (
    <div>
      {!transcript && isLoadingTranscripts ? <TextSkeleton /> : null}

      {!transcript && isErrorTranscripts ? (
        <div className="text-sm">This transcript is not yet available.</div>
      ) : null}

      {isVoiceTranscriptFragments && voiceTimeline ? (
        <TranscriptContainer>
          {voiceTimeline.map(renderVoiceTimelineItem)}
        </TranscriptContainer>
      ) : null}

      {transcript && "chat_messages" in transcript ? (
        <TranscriptContainer>
          {transcript.chat_messages.map((message) => (
            <TranscriptMessage
              key={message.id}
              speakerName={
                message.role === "user" ? props.participantName : "Agent"
              }
            >
              {message.content}
            </TranscriptMessage>
          ))}
        </TranscriptContainer>
      ) : null}
    </div>
  );
};

type VoiceTimelineItem = VoiceTimelineFragmentItem | VoiceTimelineCallItem;

interface VoiceTimelineFragmentItem {
  type: "fragment";
  time: DateTime;
  fragment: TranscriptFragment;
  /** The call ID this fragment belongs to, used as the audio player source key. */
  callId: string | null;
  /** The offset in seconds from the start of the call. */
  relativeTime: number | null;
}

interface VoiceTimelineCallItem {
  type: "call";
  time: DateTime;
  call: LessonCallDetail;
}

function buildVoiceTimeline(
  fragments: readonly TranscriptFragment[],
  callsDetails: readonly LessonCallDetail[],
): VoiceTimelineItem[] {
  const voiceTimelineCalls = callsDetails
    .map(
      (call): VoiceTimelineCallItem => ({
        type: "call",
        time: DateTime.fromISO(call.initiated_at),
        call,
      }),
    )
    .toSorted((a, b) => a.time.valueOf() - b.time.valueOf());

  const voiceTimelineFragments = fragments.map(
    (fragment): VoiceTimelineFragmentItem => {
      const time = DateTime.fromISO(fragment.start_time);

      const callItem = voiceTimelineCalls.findLast((c) => c.time <= time);
      const relativeTime = callItem
        ? time.diff(callItem.time, "seconds").as("seconds")
        : null;

      return {
        type: "fragment",
        time,
        fragment,
        callId: callItem?.call.id ?? null,
        relativeTime,
      };
    },
  );

  return [...voiceTimelineCalls, ...voiceTimelineFragments].toSorted(
    (a, b) => a.time.valueOf() - b.time.valueOf(),
  );
}

function findNextTimeOffsetSeconds(
  timeline: readonly VoiceTimelineItem[],
  index: number,
  callId: string,
): number | undefined {
  for (let i = index + 1; i < timeline.length; i++) {
    const next = timeline[i];
    if (!next) {
      continue;
    }

    if (next.type === "fragment" && next.callId === callId) {
      return next.relativeTime ?? undefined;
    }

    // If we hit a different call, stop looking
    if (next.type === "call") {
      break;
    }
  }
  return undefined;
}
